import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ user }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [grants, setGrants] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedGrant, setSelectedGrant] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [financials, setFinancials] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, grantsRes, usersRes, financialsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/grants?limit=100`),
        axios.get(`${API}/admin/users?limit=100`),
        axios.get(`${API}/admin/financials`)
      ]);

      setStats(statsRes.data);
      setGrants(grantsRes.data.grants || []);
      setUsers(usersRes.data.users || []);
      setFinancials(financialsRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      if (error.response?.status === 403) {
        alert('You do not have admin access');
      }
    }
    setLoading(false);
  };

  const handleGrantAction = async (grantId, status, notes = '') => {
    try {
      await axios.post(`${API}/admin/grants/approve`, {
        applicationId: grantId,
        status,
        notes,
        fundingAmount: status === 'funded' ? selectedGrant?.estimated_cost : null
      });

      alert(`Grant ${status} successfully!`);
      setSelectedGrant(null);
      fetchDashboardData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update grant');
    }
  };

  const handleBlockUser = async (userId, block, reason = '') => {
    try {
      await axios.post(`${API}/admin/users/block`, {
        userId,
        blocked: block,
        reason
      });

      alert(`User ${block ? 'blocked' : 'unblocked'} successfully!`);
      setSelectedUser(null);
      fetchDashboardData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to block user');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  const filteredGrants = grants.filter(g => 
    filterStatus === 'all' || g.status === filterStatus
  );

  const filteredUsers = users.filter(u =>
    u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            🏢 Admin Dashboard
          </h1>
          <p className="text-gray-600">
            Complete platform control center - Welcome, {user?.full_name}
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex space-x-8">
            {['overview', 'grants', 'users', 'financials', 'compliance'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${
                  activeTab === tab
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600 text-sm">Total Users</span>
                  <span className="text-2xl">👥</span>
                </div>
                <p className="text-3xl font-bold text-gray-900">{stats.totalUsers}</p>
                <p className="text-sm text-green-600 mt-1">+{stats.newUsers7d} this week</p>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600 text-sm">Pending Grants</span>
                  <span className="text-2xl">⏳</span>
                </div>
                <p className="text-3xl font-bold text-yellow-600">{stats.pendingGrants}</p>
                <p className="text-sm text-gray-500 mt-1">Need review</p>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600 text-sm">Total Funding</span>
                  <span className="text-2xl">💰</span>
                </div>
                <p className="text-3xl font-bold text-green-600">{formatCurrency(stats.totalFunding)}</p>
                <p className="text-sm text-gray-500 mt-1">{stats.fundedGrants} grants</p>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-600 text-sm">Blocked Users</span>
                  <span className="text-2xl">🚫</span>
                </div>
                <p className="text-3xl font-bold text-red-600">{stats.blockedUsers}</p>
                <p className="text-sm text-gray-500 mt-1">{stats.nonCompliantUsers} non-compliant</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <button
                  onClick={() => setActiveTab('grants')}
                  className="p-4 border-2 border-purple-200 hover:border-purple-400 rounded-lg transition"
                >
                  <div className="text-3xl mb-2">📝</div>
                  <div className="text-sm font-medium">Review Grants</div>
                  <div className="text-xs text-gray-500">{stats.pendingGrants} pending</div>
                </button>
                
                <button
                  onClick={() => setActiveTab('users')}
                  className="p-4 border-2 border-blue-200 hover:border-blue-400 rounded-lg transition"
                >
                  <div className="text-3xl mb-2">👥</div>
                  <div className="text-sm font-medium">Manage Users</div>
                  <div className="text-xs text-gray-500">{stats.totalUsers} total</div>
                </button>

                <button
                  onClick={() => setActiveTab('financials')}
                  className="p-4 border-2 border-green-200 hover:border-green-400 rounded-lg transition"
                >
                  <div className="text-3xl mb-2">💳</div>
                  <div className="text-sm font-medium">Financials</div>
                  <div className="text-xs text-gray-500">{formatCurrency(stats.totalFunding)}</div>
                </button>

                <button
                  onClick={() => setActiveTab('compliance')}
                  className="p-4 border-2 border-orange-200 hover:border-orange-400 rounded-lg transition"
                >
                  <div className="text-3xl mb-2">📋</div>
                  <div className="text-sm font-medium">Compliance</div>
                  <div className="text-xs text-gray-500">{stats.nonCompliantUsers} issues</div>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Grants Tab */}
        {activeTab === 'grants' && (
          <div className="space-y-4">
            {/* Filter */}
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <div className="flex space-x-3">
                {['all', 'pending', 'approved', 'funded', 'rejected'].map(status => (
                  <button
                    key={status}
                    onClick={() => setFilterStatus(status)}
                    className={`px-4 py-2 rounded-lg font-medium capitalize ${
                      filterStatus === status
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {status} ({grants.filter(g => status === 'all' || g.status === status).length})
                  </button>
                ))}
              </div>
            </div>

            {/* Grants List */}
            <div className="space-y-3">
              {filteredGrants.map(grant => (
                <div
                  key={grant.id}
                  className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 hover:border-purple-300 transition cursor-pointer"
                  onClick={() => setSelectedGrant(grant)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{grant.full_name}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          grant.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          grant.status === 'approved' ? 'bg-green-100 text-green-800' :
                          grant.status === 'funded' ? 'bg-purple-100 text-purple-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {grant.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Email:</span>
                          <p className="font-medium">{grant.email}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">State:</span>
                          <p className="font-medium">{grant.state_name}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Amount:</span>
                          <p className="font-medium">{formatCurrency(grant.estimated_cost)}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Applied:</span>
                          <p className="font-medium">{formatDate(grant.application_date)}</p>
                        </div>
                      </div>
                    </div>
                    <button className="text-purple-600 hover:text-purple-700">
                      View Details →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4">
            {/* Search */}
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* Users List */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Joined</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map(user => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-900">{user.primary_type || 'N/A'}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="px-6 py-4">
                        {user.blocked ? (
                          <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                            Blocked
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                            Active
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <button
                          onClick={() => setSelectedUser(user)}
                          className="text-purple-600 hover:text-purple-700 font-medium"
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Financials Tab */}
        {activeTab === 'financials' && financials && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <h3 className="text-sm text-gray-600 mb-2">Total Disbursed</h3>
                <p className="text-3xl font-bold text-green-600">{formatCurrency(financials.totalDisbursed)}</p>
                <p className="text-sm text-gray-500 mt-1">{financials.fundedGrantsCount} grants funded</p>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <h3 className="text-sm text-gray-600 mb-2">Pending Amount</h3>
                <p className="text-3xl font-bold text-yellow-600">{formatCurrency(financials.pendingAmount)}</p>
                <p className="text-sm text-gray-500 mt-1">Awaiting approval</p>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
                <h3 className="text-sm text-gray-600 mb-2">Average Grant</h3>
                <p className="text-3xl font-bold text-purple-600">
                  {formatCurrency(financials.fundedGrantsCount > 0 ? financials.totalDisbursed / financials.fundedGrantsCount : 0)}
                </p>
                <p className="text-sm text-gray-500 mt-1">Per application</p>
              </div>
            </div>

            {/* Monthly Funding */}
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Monthly Funding</h3>
              <div className="space-y-3">
                {financials.monthlyFunding.map(month => (
                  <div key={month.month} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium">{month.month}</span>
                    <span className="text-lg font-bold text-green-600">{formatCurrency(month.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Compliance Tab */}
        {activeTab === 'compliance' && (
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold mb-4">Non-Compliant Users</h3>
            <div className="space-y-3">
              {users.filter(u => u.user_types?.includes('home_chef_aspiring') || u.user_types?.includes('home_chef_certified')).map(user => (
                <div key={user.id} className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{user.full_name}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                    <button
                      onClick={() => handleBlockUser(user.id, true, 'Non-compliant')}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
                    >
                      Block User
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Grant Detail Modal */}
        {selectedGrant && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
              <div className="flex items-start justify-between mb-6">
                <h2 className="text-2xl font-bold">Grant Application Details</h2>
                <button
                  onClick={() => setSelectedGrant(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Full Name</label>
                    <p className="text-gray-900">{selectedGrant.full_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Email</label>
                    <p className="text-gray-900">{selectedGrant.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Phone</label>
                    <p className="text-gray-900">{selectedGrant.phone}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">SSN (Last 4)</label>
                    <p className="text-gray-900">***-**-{selectedGrant.ssn?.slice(-4) || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">State</label>
                    <p className="text-gray-900">{selectedGrant.state_name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Estimated Cost</label>
                    <p className="text-gray-900 font-bold">{formatCurrency(selectedGrant.estimated_cost)}</p>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-600">Why Chef</label>
                  <p className="text-gray-900 mt-1">{selectedGrant.why_chef}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-600">Cuisine Type</label>
                  <p className="text-gray-900">{selectedGrant.cuisine_type}</p>
                </div>

                {selectedGrant.state_id_filename && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">State ID Document</label>
                    <p className="text-purple-600">📄 {selectedGrant.state_id_filename}</p>
                  </div>
                )}

                {selectedGrant.status === 'pending' && (
                  <div className="flex space-x-3 pt-4 border-t">
                    <button
                      onClick={() => handleGrantAction(selectedGrant.id, 'approved')}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-medium"
                    >
                      ✅ Approve
                    </button>
                    <button
                      onClick={() => handleGrantAction(selectedGrant.id, 'rejected', 'Does not meet criteria')}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-medium"
                    >
                      ❌ Reject
                    </button>
                  </div>
                )}

                {selectedGrant.status === 'approved' && (
                  <button
                    onClick={() => handleGrantAction(selectedGrant.id, 'funded')}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-lg font-medium"
                  >
                    💰 Mark as Funded
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* User Detail Modal */}
        {selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-2xl w-full p-6">
              <div className="flex items-start justify-between mb-6">
                <h2 className="text-2xl font-bold">User Management</h2>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Full Name</label>
                  <p className="text-gray-900 text-lg">{selectedUser.full_name}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Email</label>
                  <p className="text-gray-900">{selectedUser.email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">User Type</label>
                  <p className="text-gray-900">{selectedUser.primary_type}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Joined</label>
                  <p className="text-gray-900">{formatDate(selectedUser.created_at)}</p>
                </div>

                <div className="flex space-x-3 pt-4 border-t">
                  {selectedUser.blocked ? (
                    <button
                      onClick={() => handleBlockUser(selectedUser.id, false)}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-medium"
                    >
                      Unblock User
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        const reason = prompt('Reason for blocking:');
                        if (reason) handleBlockUser(selectedUser.id, true, reason);
                      }}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-medium"
                    >
                      Block User
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
